// pmi-cameras.js
//
// Extracción de las cámaras de un item PMI/device/bus/lpr a partir de sus
// attributes (y, para LPR, de item.cameras top-level). Única fuente de verdad
// usada por MapDrawer (preview de cámaras en el drawer) y MapV2 (pre-warm de
// cámaras en el tray) para que ambos estén siempre alineados.
//
// API:
//   extractPmiCameras(item) → [{ devId, title, isPtz, streamUrl }]
//   toTrayCamera(item, cam)  → { id, name, streamUrl, isPtz, uniqueId, devId }
//                              (misma forma que produce pinPmiCamera / consume
//                               el store en ADD_TRAY_CAMERA)

// Soporta tres formatos:
//   - Format 1 (PMI): dev0, dev1, … como strings base64 con JSON dentro.
//   - Format 2 (Device/Bus): attributes.cameras = array de objetos.
//   - Format 2b (LPR/analytic-device): item.cameras top-level = array de objetos.
// Las cámaras PTZ se priorizan al inicio (orden relativo estable dentro de
// cada grupo) por conveniencia operativa.
export function extractPmiCameras(item) {
  if (!item) return []
  const attrs = item.attributes || {}
  let cameras = []
  const entityName = item.name || ''
  // LPR (analytic-device) no tiene uniqueId de Traccar: usa identifier.
  const uniqueId = item.uniqueId || item.identifier || ''
  // Sin id de dueño no hay streamUrl válido — mejor sección ausente que
  // un iframe con 404.
  if (!uniqueId) return []

  // Format 1: PMI — dev0, dev1, … base64(JSON)
  const devKeys = Object.keys(attrs).filter(k => {
    return k.slice(0, 3) === 'dev' && !Number.isNaN(parseInt(k.slice(3)))
  }).sort()
  for (let i = 0; i < devKeys.length; i++) {
    try {
      const decoded = JSON.parse(atob(attrs[devKeys[i]]))
      if (!decoded.server || !decoded.name) {
        // eslint-disable-next-line no-continue
        continue
      }
      cameras.push({
        devId: devKeys[i],
        title: entityName + ' - ' + (decoded.title || decoded.name || devKeys[i]),
        isPtz: decoded.isPtz || false,
        streamUrl: '/api/v2/video/stream/' + decoded.server + '/' + uniqueId + '/' + decoded.name,
      })
    } catch (e) { /* skip invalid */ }
  }

  // Format 2: Device/Bus — attributes.cameras; LPR — item.cameras top-level.
  let camsArray = null
  if (Array.isArray(attrs.cameras) && attrs.cameras.length > 0) {
    camsArray = attrs.cameras
  } else if (Array.isArray(item.cameras) && item.cameras.length > 0) {
    camsArray = item.cameras
  }
  if (camsArray) {
    cameras = []
    const maxCams = item.numberOfCameras || camsArray.length
    for (let j = 0; j < camsArray.length && cameras.length < maxCams; j++) {
      const cam = camsArray[j]
      if (!cam.server || !cam.name) {
        // eslint-disable-next-line no-continue
        continue
      }
      cameras.push({
        devId: cam.name || ('cam' + j),
        title: entityName + ' - ' + (cam.title || cam.name || 'cam' + j),
        isPtz: cam.isPtz || false,
        streamUrl: '/api/v2/video/stream/' + cam.server + '/' + uniqueId + '/' + cam.name,
      })
    }
  }

  // PTZ primero, preservando el orden relativo original dentro de cada grupo.
  return cameras
    .map((c, i) => ({ c, i }))
    .sort((a, b) => {
      if (!!a.c.isPtz === !!b.c.isPtz) return a.i - b.i
      return a.c.isPtz ? -1 : 1
    })
    .map(x => x.c)
}

// Mapea una cámara extraída a la forma que consume el tray (y el store en
// ADD_TRAY_CAMERA). El id es `${pmiId}-${devId}`, idéntico al de pinPmiCamera,
// para que pre-warm y fijado compartan identidad (no se dupliquen).
export function toTrayCamera(item, cam) {
  const ownerUniqueId = (item && item.uniqueId) || ''
  return {
    id: (item && item.id != null ? item.id : '') + '-' + cam.devId,
    name: cam.title,
    streamUrl: cam.streamUrl,
    // PTZ opera vía /api/cameras/ptz (kalan-server), que resuelve por
    // uniqueId de Traccar. Sin uniqueId (caso LPR) el control sería un
    // botón muerto en el tray → se apaga el flag.
    isPtz: (cam.isPtz && !!ownerUniqueId) || false,
    uniqueId: ownerUniqueId,
    devId: cam.devId || '',
  }
}

export default { extractPmiCameras, toTrayCamera }
